import React, { useEffect, useMemo, useState } from "react";
import GitActivityCard from "../Components/GitActivityCard";
import Commitleaderboard from "../Components/Commitleaderboard";
import "../Assests/DashboardTab.css";
import { supabase } from "../Db_Connections/db_connection";
import {
  getDashboardData,
  getAllBirthdays,
  addDashboardPost,
} from "../Db_Connections/dashboardAPi";

const TABLE_NAME = "dashboard_posts";

function DashboardTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);

  const session = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("auth"));
    } catch {
      return null;
    }
  }, []);

  const isAdmin = session?.role === "Admin";
  // const isAdmin = true;

  // =========================
  // ✅ Ticket Summary States
  // =========================
  const [ticketSummary, setTicketSummary] = useState([]);
  const [ticketSummaryLoading, setTicketSummaryLoading] = useState(true);

  const normalize = (v) => (v || "").toString().trim().toLowerCase();

  // ✅ Now summary will store only completed count (but includes assignees even if 0)
  const buildTicketSummary = (tickets, profiles) => {
    const emailToName = {};
    (profiles || []).forEach((p) => {
      const name = `${p.first_name || ""} ${p.last_name || ""}`.trim();
      if (p.email) emailToName[normalize(p.email)] = name || p.email;
    });

    const summary = {};
    (tickets || []).forEach((t) => {
      const assigneeRaw = t.assignee || "Unassigned";
      const displayName =
        emailToName[normalize(assigneeRaw)] || assigneeRaw || "Unassigned";

      if (!summary[displayName]) {
        summary[displayName] = {
          name: displayName,
          completed: 0,
        };
      }

      if (t.is_closed) summary[displayName].completed += 1;
    });

    // sort by completed desc
    return Object.values(summary).sort((a, b) => b.completed - a.completed);
  };

  // =========================
  // EDIT STATES
  // =========================
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // =========================
  // ADD QUICK LINK STATES
  // =========================
  const [showAddQuickLinkModal, setShowAddQuickLinkModal] = useState(false);

  const [newQuickLink, setNewQuickLink] = useState({
    title: "",
    link_url: "",
  });

  const [editForm, setEditForm] = useState({
    title: "",
    message: "",
    link_url: "",
    event_date: "",
  });

  // =========================
  // LOAD DATA
  // =========================
  const loadData = async () => {
    setLoading(true);

    try {
      const { data: posts, error } = await getDashboardData();

      console.log("Dashboard posts full:", posts);
      console.log("Dashboard posts error:", error);

      if (error) {
        console.error("Dashboard fetch error:", error);
        setAnnouncements([]);
        setQuickLinks([]);
        setHolidays([]);
      } else if (posts && Array.isArray(posts)) {
        const announcementData = posts.filter(
          (p) => p.type?.trim().toLowerCase() === "announcement"
        );

        const quickLinkData = posts.filter(
          (p) =>
            p.type?.trim().toLowerCase() === "quick link" &&
            p.link_url &&
            p.link_url.trim() !== ""
        );

        const holidayData = posts.filter(
          (p) => p.type?.trim().toLowerCase() === "holiday"
        );

        setAnnouncements(announcementData);
        setQuickLinks(quickLinkData);
        setHolidays(holidayData);
      } else {
        setAnnouncements([]);
        setQuickLinks([]);
        setHolidays([]);
      }

      const { data: bdays } = await getAllBirthdays();
      if (bdays) setBirthdays(bdays);

      // =========================
      // ✅ Ticket Summary Load
      // =========================
      setTicketSummaryLoading(true);

      const { data: profs, error: profErr } = await supabase
        .from("profiles")
        .select("first_name,last_name,email");

      if (profErr) console.error("Profiles fetch error:", profErr);

      const { data: tks, error: tErr } = await supabase
        .from("tickets")
        .select("assignee,is_closed,created_at")
        .order("created_at", { ascending: false });

      if (tErr) console.error("Tickets fetch error:", tErr);

      setTicketSummary(buildTicketSummary(tks || [], profs || []));
      setTicketSummaryLoading(false);
    } catch (err) {
      console.error("loadData error:", err);
      setTicketSummary([]);
      setTicketSummaryLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // =========================
  // EDIT FUNCTIONS
  // =========================
  const openEdit = (item) => {
    setEditingItem(item);
    setEditForm({
      title: item.title || "",
      message: item.message || "",
      link_url: item.link_url || "",
      event_date: item.event_date || "",
    });
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditingItem(null);
  };

  const handleEditChange = (key, value) =>
    setEditForm((prev) => ({ ...prev, [key]: value }));

  // =========================
  // ADD QUICK LINK FUNCTIONS
  // =========================
  const openAddQuickLinkModal = () => {
    setNewQuickLink({
      title: "",
      link_url: "",
    });
    setShowAddQuickLinkModal(true);
  };

  const closeAddQuickLinkModal = () => {
    setShowAddQuickLinkModal(false);
  };

  const handleNewQuickLinkChange = (key, value) => {
    setNewQuickLink((prev) => ({ ...prev, [key]: value }));
  };

  // =========================
  // VALIDATIONS
  // =========================
  const validateEdit = () => {
    if (!editForm.title.trim()) return "Title is required";
    if (editingItem?.type === "Announcement" && !editForm.message.trim())
      return "Message is required";
    if (editingItem?.type === "Quick Link" && !editForm.link_url.trim())
      return "Link URL is required";
    if (editingItem?.type === "Holiday" && !editForm.event_date.trim())
      return "Event date is required";
    return null;
  };

  const validateNewQuickLink = () => {
    if (!newQuickLink.title.trim()) return "Title is required";
    if (!newQuickLink.link_url.trim()) return "Link URL is required";
    return null;
  };

  // =========================
  // SAVE NEW QUICK LINK
  // =========================
  const saveNewQuickLink = async () => {
    const err = validateNewQuickLink();
    if (err) return alert(err);

    setSaving(true);

    try {
      const formattedUrl = newQuickLink.link_url.startsWith("http")
        ? newQuickLink.link_url
        : `https://${newQuickLink.link_url}`;

      const { error } = await addDashboardPost({
        type: "Quick Link",
        title: newQuickLink.title.trim(),
        link_url: formattedUrl,
      });

      if (error) throw error;

      closeAddQuickLinkModal();
      await loadData();
    } catch (e) {
      console.error("Insert error:", e);
      alert(e?.message || "Quick Link add failed");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // SAVE EDIT
  // =========================
  const saveEdit = async () => {
    if (!editingItem) return;

    const err = validateEdit();
    if (err) return alert(err);

    setSaving(true);
    try {
      let payload = { title: editForm.title };

      if (editingItem.type === "Announcement") {
        payload.message = editForm.message;
      } else if (editingItem.type === "Quick Link") {
        payload.link_url = editForm.link_url;
      } else if (editingItem.type === "Holiday") {
        payload.event_date = editForm.event_date;
      }

      const { error } = await supabase
        .from(TABLE_NAME)
        .update(payload)
        .eq("id", editingItem.id);

      if (error) throw error;

      closeEdit();
      await loadData();
    } catch (e) {
      console.error("Update error:", e);
      alert(e?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // DELETE
  // =========================
  const deleteItem = async (item) => {
    const ok = window.confirm(`Delete "${item.title}" ?`);
    if (!ok) return;

    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .delete()
        .eq("id", item.id);

      if (error) throw error;

      await loadData();
    } catch (e) {
      console.error("Delete error:", e);
      alert(e?.message || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "20px", color: "white" }}>
        Loading Dashboard...
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dbtab-top-bar">
        <h3>Dashboard Overview</h3>
        <button className="refresh-btn" onClick={loadData}>
          ↻
        </button>
      </div>

      <div className="dashboard-grid">
        {/* BIRTHDAY CARD */}
        <div className="dbtab-card dbbday-card">
          <h3>🎂 Upcoming Birthdays</h3>
          <div className="dbbday-card-content">
            {birthdays.length === 0 ? (
              <p>No birthday records found</p>
            ) : (
              birthdays.map((user) => {
                const dob = new Date(user.dob);
                const today = new Date();

                const isTodayBirthday =
                  dob.getDate() === today.getDate() &&
                  dob.getMonth() === today.getMonth();

                return (
                  <div
                    key={user.id}
                    className={`dbbday-item ${isTodayBirthday ? "dbbday-item--today" : ""
                      }`}
                  >
                    {isTodayBirthday && (
                      <div className="dbbday-ribbon">🎀 Birthday Today</div>
                    )}

                    <div className="dbbday-left">
                      <span className="dbbday-icon">
                        {isTodayBirthday ? "🎂" : "🎉"}
                      </span>
                      <span className="dbbday-name">
                        {user.first_name} {user.last_name}
                      </span>
                    </div>

                    <span className="dbbday-date">
                      {isTodayBirthday
                        ? "🎉 Today"
                        : new Date(user.dob).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "long",
                        })}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* QUICK LINKS CARD */}
        <div className="dbtab-card quick-links-card">
          <div className="dbtab-row-between">
            <h3>🔗 Quick Links</h3>

            {isAdmin && (
              <button
                className="dbtab-btn-primary"
                onClick={openAddQuickLinkModal}
              >
                + Add
              </button>
            )}
          </div>

          <div className="dbtab-card-content">
            {quickLinks.length === 0 ? (
              <p>No links available</p>
            ) : (
              quickLinks.map((link) => (
                <div
                  key={link.id}
                  className="quick-link-item dbtab-row-between"
                >
                  <div className="quick-link-anchor">
                    <span className="quick-link-title">{link.title}</span>

                    <a
                      href={
                        link.link_url?.startsWith("http")
                          ? link.link_url
                          : `https://${link.link_url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="quick-link-url"
                      title={
                        link.link_url?.startsWith("http")
                          ? link.link_url
                          : `https://${link.link_url}`
                      }
                    >
                      {link.link_url?.startsWith("http")
                        ? link.link_url
                        : `https://${link.link_url}`}
                    </a>
                  </div>

                  {isAdmin && (
                    <div className="dbtab-row-actions">
                      <button
                        className="dbtab-icon-btn"
                        onClick={() => openEdit(link)}
                      >
                        ✏️
                      </button>
                      <button
                        className="dbtab-icon-btn dbtab-delete-btn"
                        onClick={() => deleteItem(link)}
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ANNOUNCEMENTS CARD */}
        <div className="dbtab-card announcements-card">
          <h3>📢 Announcements</h3>
          <div className="dbtab-card-content">
            {announcements.length === 0 ? (
              <p>No new announcements</p>
            ) : (
              announcements.map((a) => (
                <div key={a.id} className="announcement-item">
                  <div className="dbtab-row-between">
                    <p className="announcement-title">{a.title}</p>

                    {isAdmin && (
                      <div className="dbtab-row-actions">
                        <button
                          className="dbtab-icon-btn"
                          onClick={() => openEdit(a)}
                        >
                          ✏️
                        </button>
                        <button
                          className="dbtab-icon-btn dbtab-delete-btn"
                          onClick={() => deleteItem(a)}
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="announcement-desc">{a.message}</p>
                  <span className="time-ago">
                    {new Date(a.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* UPCOMING HOLIDAYS CARD */}
        <div className="dbtab-card leave-report-card">
          <h3>🎉 Upcoming Holidays</h3>
          <div className="dbtab-card-content">
            {holidays.length === 0 ? (
              <p>No holidays posted</p>
            ) : (
              holidays.map((h) => (
                <div key={h.id} className="holiday-item dbtab-row-between">
                  <div>
                    <span className="holiday-date">
                      {new Date(h.event_date).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>{" "}
                    <span className="holiday-name">{h.title}</span>
                  </div>

                  {isAdmin && (
                    <div className="dbtab-row-actions">
                      <button
                        className="dbtab-icon-btn"
                        onClick={() => openEdit(h)}
                      >
                        ✏️
                      </button>
                      <button
                        className="dbtab-icon-btn dbtab-delete-btn"
                        onClick={() => deleteItem(h)}
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="git-section-row">
        <div className="git-activity-wrapper">
          <GitActivityCard />
        </div>

        <div className="git-commit-wrapper">
          {/* ✅ Ticket Summary (NOW on top) */}
          <div className="dbtab-card ticket-summary-card">
            <h3>🧾 Ticket Summary</h3>

            {ticketSummaryLoading ? (
              <p>Loading ticket summary...</p>
            ) : ticketSummary.length === 0 ? (
              <p>No ticket data found</p>
            ) : (
              <div className="ticket-summary-table-wrap">
                <table className="ticket-summary-table">
                  <thead>
                    <tr>
                      <th>Assignee</th>
                      <th>Completed Tasks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketSummary.map((row) => (
                      <tr key={row.name}>
                        <td>{row.name}</td>
                        <td>{row.completed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ✅ Daily Commits (NOW below) */}
          <Commitleaderboard />
        </div>
      </div>
      {/* EDIT MODAL */}
      {isAdmin && showEditModal && editingItem && (
        <div className="dbtab-modal-overlay" onClick={closeEdit}>
          <div className="dbtab-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dbtab-modal-header">
              <h2 className="dbtab-modal-title">Edit {editingItem.type}</h2>
              <button className="dbtab-close-btn" onClick={closeEdit}>
                ×
              </button>
            </div>

            <div className="dbtab-modal-body">
              <div className="dbtab-form-group">
                <label className="dbtab-form-label">Title *</label>
                <input
                  className="dbtab-form-input"
                  value={editForm.title}
                  onChange={(e) => handleEditChange("title", e.target.value)}
                />
              </div>

              {editingItem.type === "Announcement" && (
                <div className="dbtab-form-group">
                  <label className="dbtab-form-label">Message *</label>
                  <textarea
                    className="dbtab-form-textarea"
                    value={editForm.message}
                    onChange={(e) =>
                      handleEditChange("message", e.target.value)
                    }
                  />
                </div>
              )}

              {editingItem.type === "Quick Link" && (
                <div className="dbtab-form-group">
                  <label className="dbtab-form-label">Link URL *</label>
                  <input
                    className="dbtab-form-input"
                    value={editForm.link_url}
                    onChange={(e) =>
                      handleEditChange("link_url", e.target.value)
                    }
                  />
                </div>
              )}

              {editingItem.type === "Holiday" && (
                <div className="dbtab-form-group">
                  <label className="dbtab-form-label">Event Date *</label>
                  <input
                    type="date"
                    className="dbtab-form-input"
                    value={editForm.event_date}
                    onChange={(e) =>
                      handleEditChange("event_date", e.target.value)
                    }
                  />
                </div>
              )}
            </div>

            <div className="dbtab-modal-footer">
              <button
                className="dbtab-btn-secondary"
                onClick={closeEdit}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="dbtab-btn-primary"
                onClick={saveEdit}
                disabled={saving}
              >
                {saving ? "Saving..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD QUICK LINK MODAL */}
      {isAdmin && showAddQuickLinkModal && (
        <div
          className="dbtab-modal-overlay"
          onClick={closeAddQuickLinkModal}
        >
          <div className="dbtab-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dbtab-modal-header">
              <h2 className="dbtab-modal-title">Add Quick Link</h2>
              <button
                className="dbtab-close-btn"
                onClick={closeAddQuickLinkModal}
              >
                ×
              </button>
            </div>

            <div className="dbtab-modal-body">
              <div className="dbtab-form-group">
                <label className="dbtab-form-label">Title *</label>
                <input
                  className="dbtab-form-input"
                  value={newQuickLink.title}
                  onChange={(e) =>
                    handleNewQuickLinkChange("title", e.target.value)
                  }
                  placeholder="Enter link title"
                />
              </div>

              <div className="dbtab-form-group">
                <label className="dbtab-form-label">Link URL *</label>
                <input
                  className="dbtab-form-input"
                  value={newQuickLink.link_url}
                  onChange={(e) =>
                    handleNewQuickLinkChange("link_url", e.target.value)
                  }
                  placeholder="Enter website URL"
                />
              </div>
            </div>

            <div className="dbtab-modal-footer">
              <button
                className="dbtab-btn-secondary"
                onClick={closeAddQuickLinkModal}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                className="dbtab-btn-primary"
                onClick={saveNewQuickLink}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardTab;